import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Store, Package, Clock, ClipboardList, Truck, ChevronDown, ShoppingCart } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface ShopSection {
  label: string;
  href: string;
  icon: LucideIcon;
}

function getShopSections(farmId: string): ShopSection[] {
  const base = `/app/producer/farm/${farmId}/shop`;
  return [
    { label: 'Om gårdsbutiken', href: base, icon: Store },
    { label: 'Produkter', href: `${base}/products`, icon: Package },
    { label: 'Beställningar', href: `${base}/orders`, icon: ShoppingCart },
    { label: 'Upphämtning', href: `${base}/pickup-windows`, icon: Clock },
    { label: 'Packlista', href: `${base}/pack-list`, icon: ClipboardList },
    { label: 'Frakt & leverans', href: `${base}/shipping`, icon: Truck },
  ];
}

/**
 * Mobile-only dropdown section switcher for farm shop sub-pages.
 * Renders below the topbar when on narrow layouts inside /shop routes.
 */
export function ShopMobileSectionNav() {
  const { farmId } = useParams<{ farmId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!farmId) return null;

  const sections = getShopSections(farmId);
  const shopBase = `/app/producer/farm/${farmId}/shop`;

  // Only show when inside shop routes
  if (!pathname.startsWith(shopBase)) return null;

  // Find active section
  const active = sections.find((s) => {
    if (s.href === shopBase) return pathname === shopBase;
    return pathname.startsWith(s.href);
  }) ?? sections[0];

  const ActiveIcon = active.icon;

  return (
    <div className="sticky top-14 z-20 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 px-4 py-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="h-9 gap-2 px-3 text-sm font-medium text-foreground w-full justify-between"
          >
            <span className="flex items-center gap-2 min-w-0">
              <ActiveIcon className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">{active.label}</span>
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = section.href === shopBase
              ? pathname === shopBase
              : pathname.startsWith(section.href);
            return (
              <DropdownMenuItem
                key={section.href}
                onClick={() => navigate(section.href)}
                className={cn(
                  'gap-2 cursor-pointer',
                  isActive && 'bg-accent font-medium'
                )}
              >
                <Icon className="h-4 w-4 shrink-0" />
                <span>{section.label}</span>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
